import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from "@angular/common/http";
import { Observable } from "rxjs";

/**
 * CSRF Protection Interceptor
 *
 * This interceptor adds the X-CSRF-Token header to all state-changing requests
 * (POST, PUT, DELETE, PATCH) by reading the token from the BDPCSRF cookie.
 *
 * The CSRF token is set by the server on login and must be included in
 * all mutating requests to protect against Cross-Site Request Forgery attacks.
 */
@Injectable()
export class CSRFInterceptor implements HttpInterceptor {
  private readonly CSRF_COOKIE_NAME = "BDPCSRF";
  private readonly CSRF_HEADER_NAME = "X-CSRF-Token";

  // Methods that require CSRF protection (state-changing methods)
  private readonly PROTECTED_METHODS = ["POST", "PUT", "DELETE", "PATCH"];

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Only add CSRF token for state-changing requests
    if (!this.PROTECTED_METHODS.includes(req.method.toUpperCase())) {
      return next.handle(req);
    }

    // Skip CSRF for external URLs (only protect our own API)
    if (!this.isInternalRequest(req.url)) {
      return next.handle(req);
    }

    // Get CSRF token from cookie
    const csrfToken = this.getCSRFToken();

    if (csrfToken) {
      // Clone the request and add the CSRF header
      const modifiedReq = req.clone({
        setHeaders: {
          [this.CSRF_HEADER_NAME]: csrfToken,
        },
      });
      return next.handle(modifiedReq);
    }

    // No token found, proceed without it (server will reject if required)
    return next.handle(req);
  }

  /**
   * Reads the CSRF token from the cookie
   */
  private getCSRFToken(): string | null {
    const name = this.CSRF_COOKIE_NAME + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(";");

    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.indexOf(name) === 0) {
        return cookie.substring(name.length);
      }
    }
    return null;
  }

  /**
   * Checks if the request is for our internal API
   * We only add CSRF tokens to requests to our own backend
   */
  private isInternalRequest(url: string): boolean {
    // If it's a relative URL, it's internal
    if (url.startsWith("/")) {
      return true;
    }

    // Check if it's one of our allowed domains
    const allowedDomains = [
      "bidapass.com",
      "bida.dev",
      "bida.net",
      "localhost",
      "127.0.0.1",
    ];

    try {
      const urlObj = new URL(url);
      return allowedDomains.some(
        (domain) =>
          urlObj.hostname === domain || urlObj.hostname.endsWith("." + domain),
      );
    } catch {
      // If URL parsing fails, assume it's internal
      return true;
    }
  }
}
